import React, { useState, useEffect, useContext } from "react";
// UI Material

import { Box, Typography, Fade } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Card from "components/Card/Card";
import CardContent from "@material-ui/core/CardContent";
import CardFooter from "components/Card/CardFooter";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import AuthContext from "contexts/AuthProvider.js";
import { cardTitle } from "assets/jss/material-dashboard-pro-react.js";
// Icons

import { Assessment, Pause } from "@material-ui/icons";
// Donut chart

import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
// Tooltip

import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { useTranslation } from "react-i18next";

// ====== Date KPI ======

const kpiData = [
  {
    kpiName: "Pipeline Total",
    meta: 400, // Goal in Millions (R $ 2 Billion)

    real: 254, // Held in Millions (R $ 0.2 BI)

    unit: "R$", // Unit of Measurement
  },
  {
    kpiName: "Ideação + Sustentação",
    meta: 400, // R $ 50 mi

    real: 65, // R $ 45 mi

    unit: "R$",
  },
  {
    kpiName: "Em Implementação",
    meta: 400, // R $ 500 mi

    real: 143, // R $ 200 mi

    unit: "R$",
  },
  {
    kpiName: "Capturado",
    meta: 400, // R $ 50 mi

    real: 30, // R $ 45 mi

    unit: "R$",
  },
  {
    kpiName: "Em sustentação",
    meta: 400, // R $ 50 mi

    real: 65, // R $ 45 mi

    unit: "R$",
  },
];

// ====== STYLES ======

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    flex: 1,
    height: "85%",
    position: "relative", // To position "pause" icon
  },
  cardIconTitle: {
    ...cardTitle,
    marginTop: "0px",
    marginBottom: "0px",
    padding: "10px",
    fontSize: "14px",
  },
  content: {
    display: "flex",
    flexDirection: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    height: "100%",
    marginTop: "10px",
  },
  chartContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  chartWrapper: {
    width: 60, // Donut size

    height: 60,
    marginRight: theme.spacing(2),
    cursor: "pointer", // Hover
  },
  dotsContainer: {
    display: "flex",
    justifyContent: "center",
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    margin: theme.spacing(0, 0.3),
    transition: "background-color 0.5s",
    cursor: "pointer", // Now clickable
  },
  footer: {
    padding: theme.spacing(1, 2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  pauseIcon: {
    position: "absolute",
    bottom: "5px",
    right: "5px",
    color: "#999",
    cursor: "default",
    opacity: 0.8,
  },
}));

function KpiRotatingCard() {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#2196f3";
  const classes = useStyles();
  // ESLINT-DISABLE-NEXT-LINE NO-UNUSED-VARS

  const { t } = useTranslation();

  // Current Screen Index

  const [currentIndex, setCurrentIndex] = useState(0);
  // Force the fade in each exchange

  const [fadeKey, setFadeKey] = useState(0);
  // Flag to pause the rotation

  const [isPaused, setIsPaused] = useState(false);

  // Rotation effect every 5 seconds, only if not paused

  useEffect(() => {
    if (kpiData.length <= 1 || isPaused) return; // If you only have 1 kPi or is paused, do not rotate

    const intervalId = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = (prev + 1) % kpiData.length;
        setFadeKey((oldKey) => oldKey + 1);
        return next;
      });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [isPaused]);

  // If there is no KPI, you return something

  if (!kpiData.length) {
    return <div>{t("whatshappening.messages.no_kpi")}</div>;
  }

  // Current KPI KPI

  const currentKPI = kpiData[currentIndex];

  // Calculate % performed

  const percentage =
    currentKPI.meta === 0
      ? 0
      : Math.round((currentKPI.real / currentKPI.meta) * 100);

  const metaFormatada =
    currentKPI.unit === "R$"
      ? `R$ ${currentKPI.meta} Mi`
      : currentKPI.unit === "%"
        ? `${currentKPI.meta}%`
        : `${currentKPI.meta}`;

  // Royal formatted

  const realFormatado =
    currentKPI.unit === "R$"
      ? `R$ ${currentKPI.real} Mi`
      : currentKPI.unit === "%"
        ? `${currentKPI.real}%`
        : `${currentKPI.real}`;

  return (
    <Card
      className={classes.cardRoot}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <CardHeader color="client" icon>
        <CardIcon color="client">
          <Assessment />
        </CardIcon>
        <h4 className={classes.cardIconTitle}>
          {t("whatshappening.cards_title.key_metrics")}
        </h4>
      </CardHeader>
      <CardContent className={classes.content}>
        <Fade key={fadeKey} in timeout={500}>
          <Box>
            {/* Thread Chart + KPI Name + Value */}
            <Box className={classes.chartContainer}>
              <Tippy
                content={t("whatshappening.tooltip.kpi_info", {
                  meta: metaFormatada,
                  real: realFormatado,
                })}
                arrow={true}
                theme="light"
              >
                <div className={classes.chartWrapper}>
                  <CircularProgressbar
                    value={percentage}
                    text={`${percentage}%`}
                    strokeWidth={10}
                    styles={buildStyles({
                      textSize: "25px",
                      textColor: "#666",
                      pathColor: clientColor, // filling color

                      trailColor: "#e0e0e0", // "background" color

                      backgroundColor: "#fff",
                    })}
                  />
                </div>
              </Tippy>

              {/* KPI Name and Value */}
              <Box style={{ paddingRight: "0px" }}>
                <Typography
                  variant="subtitle1"
                  style={{
                    fontSize: "15px",
                    fontWeight: 800, // Bold

                    marginBottom: 4,
                  }}
                >
                  {currentKPI.kpiName}
                </Typography>
                <Typography
                  variant="body2"
                  style={{ fontSize: "35px", color: "#555" }}
                >
                  {realFormatado}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>
      </CardContent>
      {/* Footer with dots */}
      <CardFooter className={classes.footer}>
        <Box className={classes.dotsContainer}>
          {kpiData.map((_, i) => (
            <Box
              key={i}
              className={classes.dot}
              style={{
                backgroundColor: i === currentIndex ? clientColor : "#ccc",
              }}
              onClick={() => {
                setCurrentIndex(i);
                setFadeKey((old) => old + 1);
              }}
            />
          ))}
        </Box>
      </CardFooter>
      {/* Pause icon (when paused) */}
      <Fade in={isPaused} timeout={500}>
        <Box className={classes.pauseIcon}>
          <Pause fontSize="small" />
        </Box>
      </Fade>
    </Card>
  );
}

export default KpiRotatingCard;
